import { Component, Signal, computed, input, signal } from "@angular/core";
import { CommonModule, NgClass } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";

import { VaultItemGroup } from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import {
  StoreModelNameByVaultCode,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { TransactionStoreModel } from "@bitwarden/web-vault/app/models/store/transaction.store.model";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import {
  VaultCleanupResultDialogComponent,
  CleanupGroupResult,
  AccountCascadeDeletedTx,
  AccountCascadeDeletedSource,
} from "../../vault-cleanup-result-dialog.component";
import { VaultJsonObjectDialogComponent } from "../../vault-json-object-dialog.component";
import {
  VaultDedupStyleInfoDialogComponent,
  StyleInfoBlock,
} from "../../vault-dedup-style-info-dialog.component";
import { VaultDataTypeBase } from "../vault-data-type-base";
import { GlossScrollDirective } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.directive";

/** Always excluded from account duplication hash (short names per domain). */
export const ACCT_DEFAULT_EXCLUDES = ["dc", "dm", "vid", "v", "st", "del"];

export type AcctDedupStyleValue = "A" | "B" | "C" | "D";

export interface AcctDedupStyleOption {
  value: AcctDedupStyleValue;
  label: string;
  description: string;
  excludeKeys: string[];
}

export const ACCT_DEDUP_STYLES: AcctDedupStyleOption[] = [
  {
    value: "A",
    label: "Style-A",
    description: "A total duplicate, safe to delete",
    excludeKeys: [...ACCT_DEFAULT_EXCLUDES],
  },
  {
    value: "B",
    label: "Style-B",
    description:
      "Id is not used for duplication — genuine duplicates with different ids. Safe to delete",
    excludeKeys: [...ACCT_DEFAULT_EXCLUDES, "id", "_id"],
  },
  {
    value: "C",
    label: "Style-C",
    description:
      "Timezone is not compared for duplication; accounts are duplicates regardless of tz. Safe to delete",
    excludeKeys: [...ACCT_DEFAULT_EXCLUDES, "tz"],
  },
  {
    value: "D",
    label: "Style-D",
    description:
      "Id and tz excluded; everything else compared. Same data — ids and timezones may differ. Safe to delete",
    excludeKeys: [...ACCT_DEFAULT_EXCLUDES, "id", "_id", "tz"],
  },
];

const ACCT_STYLE_INFO: Record<string, StyleInfoBlock> = {
  A: {
    title: "Style-A",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Duplicate check excludes defaults only: dateCreated (dc), dateModified (dm), vaultId (vid), version (v), and status (st).",
      "Everything that counts is the same, so it is safe to delete duplicates.",
    ],
  },
  B: {
    title: "Style-B",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Duplicate check excludes defaults and id — id is not used when comparing.",
      "Anything that counts here is the same; id might or might not differ between rows.",
    ],
  },
  C: {
    title: "Style-C",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Duplicate check excludes defaults and timezone (tz).",
      "Timezone may differ but everything else matches, including id.",
    ],
  },
  D: {
    title: "Style-D",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Duplicate check excludes defaults, id, and timezone (tz).",
      "Same data overall; ids and timezones might differ. Compare with other styles to interpret results.",
    ],
  },
};

@Component({
  selector: "app-vault-type-account",
  standalone: true,
  imports: [GlossScrollDirective, CommonModule, NgClass, MatCheckboxModule, MatButtonModule],
  templateUrl: "./vault-type-account.component.html",
})
export class VaultTypeAccountComponent extends VaultDataTypeBase {
  readonly vaultType = input.required<VaultCodeEnum>();
  protected readonly _vaultType: Signal<VaultCodeEnum> = this.vaultType;

  override readonly vaultDuplicatesOnly = signal(true);

  readonly selectedDedupStyle = signal<AcctDedupStyleValue>("A");
  readonly dedupStyles = ACCT_DEDUP_STYLES;

  readonly selectedDedupStyleDescription = computed(() => {
    const style = ACCT_DEDUP_STYLES.find((s) => s.value === this.selectedDedupStyle());
    return style?.description ?? "";
  });

  private _groupCache = new Map<AcctDedupStyleValue, VaultItemGroup[]>();
  private _cachedItemsRef: StoreData[] | null = null;

  override readonly vaultItemGroups = computed((): VaultItemGroup[] => {
    const type = this._vaultType();
    if (!type) {
      return [];
    }
    const items = this.vaultExplorer.getItemsByType(type);

    if (items !== this._cachedItemsRef) {
      this._groupCache.clear();
      this._cachedItemsRef = items;
    }

    const style = this.selectedDedupStyle();
    const cached = this._groupCache.get(style);
    if (cached) {
      return cached;
    }

    const config = ACCT_DEDUP_STYLES.find((s) => s.value === style)!;
    const groups = this.duplicatesService.getGroupedItemsCustom(type, config.excludeKeys);
    this._groupCache.set(style, groups);
    return groups;
  });

  onDedupStyleChange(ev: Event): void {
    const el = ev.target as HTMLSelectElement;
    this.selectedDedupStyle.set(el.value as AcctDedupStyleValue);
  }

  openDedupStyleInfoDialog(): void {
    this.dialog.open(VaultDedupStyleInfoDialogComponent, {
      width: "min(700px, 95vw)",
      maxHeight: "90vh",
      data: { selectedStyle: this.selectedDedupStyle(), styleInfoMap: ACCT_STYLE_INFO },
    });
  }

  openDetails(group: VaultItemGroup): void {
    this.dialog.open(VaultJsonObjectDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { title: "Account item", item: group.representative },
    });
  }

  override async removeAllDuplicates(): Promise<void> {
    const type = this._vaultType();
    if (!type || this.vaultDuplicateGroups().length === 0) {
      return;
    }
    const groupCount = this.vaultDuplicateGroups().length;
    const dupRecords = this.vaultTotalDuplicateRecordsToRemove();
    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.removeAllVaultDuplicatesForType,
      {
        messageReplacement: `Remove ${dupRecords} duplicate account row(s) across ${groupCount} group(s)? The newest row per group is kept; linked transactions and orphaned sources may also be removed.`,
      },
    );
    if (!confirmed) {
      return;
    }

    const config = ACCT_DEDUP_STYLES.find((s) => s.value === this.selectedDedupStyle())!;
    const allDupGroups = this.duplicatesService
      .getGroupedItemsCustom(type, config.excludeKeys)
      .filter((g) => g.count > 1);

    await this.executeAccountCleanup(type, allDupGroups);
  }

  override async onItemClick(group: VaultItemGroup): Promise<void> {
    if (group.count <= 1) {
      return;
    }
    const type = this._vaultType();
    if (!type) {
      return;
    }

    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.deletingVaultDuplicates,
      {
        messageReplacement: `This item has ${group.count} copies. Delete duplicate account rows and keep the newest? Linked transactions pointing at removed accounts (and orphaned source transactions) will be removed too.`,
      },
    );
    if (!confirmed) {
      return;
    }

    await this.executeAccountCleanup(type, [group]);
  }

  override async performCleanup(): Promise<void> {
    const type = this._vaultType();
    if (!type) {
      return;
    }
    const allGroups = this.vaultDuplicateGroups();
    if (allGroups.length === 0) {
      return;
    }
    const count = this.vaultCleanupCount() ?? allGroups.length;
    const groupsToClean = allGroups.slice(0, count);

    await this.executeAccountCleanup(type, groupsToClean);
  }

  private async executeAccountCleanup(
    type: VaultCodeEnum,
    groups: VaultItemGroup[],
  ): Promise<void> {
    const results: CleanupGroupResult[] = [];
    const accountsToDelete: StoreData[] = [];
    const keptAccountIds = new Set<string>();

    for (const group of groups) {
      if (group.count <= 1) {
        continue;
      }
      const sorted = [...group.all].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );
      const kept = sorted[0];
      const deleted = sorted.slice(1);
      results.push({ kept, deleted });
      accountsToDelete.push(...deleted);
      const kid = String(kept.id ?? "").trim();
      if (kid) {
        keptAccountIds.add(kid);
      }
    }

    if (accountsToDelete.length === 0) {
      return;
    }

    const accountBizIdsForCascade = new Set<string>();
    for (const acc of accountsToDelete) {
      const id = String(acc.id ?? "").trim();
      if (id) {
        accountBizIdsForCascade.add(id);
      }
    }
    for (const kid of keptAccountIds) {
      accountBizIdsForCascade.delete(kid);
    }

    const allTransactions = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.Transaction);
    const txsToDelete = allTransactions.filter((tx) => {
      const acId = String((tx as TransactionStoreModel).acId ?? "").trim();
      return acId !== "" && accountBizIdsForCascade.has(acId);
    });

    const deletedTxIds = new Set(txsToDelete.map((t) => String(t.id ?? "").trim()).filter(Boolean));

    const candidateSrIds = new Set<string>();
    for (const tx of txsToDelete) {
      const sr = String((tx as TransactionStoreModel).srId ?? "").trim();
      if (sr) {
        candidateSrIds.add(sr);
      }
    }

    for (const srId of [...candidateSrIds]) {
      const stillLinked = allTransactions.some((tx) => {
        const txId = String(tx.id ?? "").trim();
        const txSr = String((tx as TransactionStoreModel).srId ?? "").trim();
        return txSr === srId && !deletedTxIds.has(txId);
      });
      if (stillLinked) {
        candidateSrIds.delete(srId);
      }
    }

    const allSources = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.SourceTransaction);
    const sourcesToDelete = allSources.filter((s) => {
      const sid = String(s.id ?? "").trim();
      return sid !== "" && candidateSrIds.has(sid);
    });

    const deletedAccountsByBizId = new Map<string, StoreData>();
    for (const acc of accountsToDelete) {
      const bid = String(acc.id ?? "").trim();
      if (bid && !deletedAccountsByBizId.has(bid)) {
        deletedAccountsByBizId.set(bid, acc);
      }
    }

    const deletedTransactions: AccountCascadeDeletedTx[] = txsToDelete.map((tx) => {
      const acId = String((tx as TransactionStoreModel).acId ?? "").trim();
      const formerAccount =
        (acId ? deletedAccountsByBizId.get(acId) : undefined) ?? accountsToDelete[0];
      return { transaction: tx, formerAccount };
    });

    const deletedSources: AccountCascadeDeletedSource[] = [];
    const seenSrc = new Set<string>();
    for (const src of sourcesToDelete) {
      const sid = String(src.id ?? "").trim();
      if (!sid || seenSrc.has(sid)) {
        continue;
      }
      seenSrc.add(sid);
      const tx = txsToDelete.find(
        (t) => String((t as TransactionStoreModel).srId ?? "").trim() === sid,
      );
      if (tx) {
        deletedSources.push({ source: src, formerTransaction: tx });
      }
    }

    try {
      const txModel = StoreModelNameByVaultCode[VaultCodeEnum.Transaction];
      const acModel = StoreModelNameByVaultCode[type];
      const srcModel = StoreModelNameByVaultCode[VaultCodeEnum.SourceTransaction];

      if (txsToDelete.length > 0) {
        await this.vaultExplorer.deleteVaultItems(txModel, txsToDelete);
      }
      await this.vaultExplorer.deleteVaultItems(acModel, accountsToDelete);
      if (sourcesToDelete.length > 0) {
        await this.vaultExplorer.deleteVaultItems(srcModel, sourcesToDelete);
      }

      await this.vaultExplorer.reloadVaultDataForType(VaultCodeEnum.Transaction);
      await this.vaultExplorer.reloadVaultDataForType(type);
      await this.vaultExplorer.reloadVaultDataForType(VaultCodeEnum.SourceTransaction);

      this.clearGroupCache();
    } catch {
      this.confirmationService.notify(NotificationEnum.error, "error", "Cleanup failed.", false);
      return;
    }

    this.dialog.open(VaultCleanupResultDialogComponent, {
      width: "min(1100px, 95vw)",
      maxHeight: "90vh",
      data: {
        groups: results,
        accountCleanup: {
          deletedTransactions,
          deletedSources,
        },
      },
    });
  }

  private clearGroupCache(): void {
    this._groupCache.clear();
    this._cachedItemsRef = null;
  }
}
